import React, { useState, useEffect, useRef } from 'react';
import { taskDateKey } from '../utils/taskSchedule';
import { isPersonalTask } from '../utils/taskKind';
import './WeekCalendarView.css';

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DAY_ABBR = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'];

const formatDayHeader = (date) => {
  const d = new Date(date);
  const day = d.getDate();
  const month = d.getMonth();
  const monthShort = ['янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июн.', 'июл.', 'авг.', 'сен.', 'окт.', 'нояб.', 'дек.'][month];
  const dayOfWeek = DAY_ABBR[d.getDay()];
  return `${day} ${monthShort} ${dayOfWeek}`;
};

const getDateKey = (date) => {
  const storedKey = taskDateKey(date);
  if (storedKey) return storedKey;
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getWeekDates = (refDate) => {
  const start = new Date(refDate);
  start.setHours(0, 0, 0, 0);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(start);
    x.setDate(start.getDate() + i);
    dates.push(x);
  }
  return dates;
};

const addWeeks = (date, weeks) => {
  const d = new Date(date);
  d.setDate(d.getDate() + weeks * 7);
  return d;
};

const toDateKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const taskBlockLine = (task) => `${isPersonalTask(task) ? '👤 ' : ''}${task.title || ''}`;

const taskBlockClass = (task) => [
  'gnom-task-block',
  task.status === 'completed' ? 'gnom-task-block-completed' : '',
  isPersonalTask(task) ? 'gnom-task-block-personal' : '',
].filter(Boolean).join(' ');

const taskBlockStyle = (task) => ({
  background: isPersonalTask(task)
    ? 'linear-gradient(135deg, #6D28D9, #A21CAF)'
    : (task.color || '#4CAF50'),
});

export default function WeekCalendarView({ tasks = [], weekStartKey, setWeekStartKey, onTaskClick, onAddTask }) {
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const monthDropdownRef = useRef(null);
  const swipeStartX = useRef(null);
  const weekStartKeyRef = useRef(weekStartKey);
  weekStartKeyRef.current = weekStartKey;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target)) setMonthDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const goPrevWeek = () => setWeekStartKey(toDateKey(addWeeks(new Date(weekStartKeyRef.current || weekStartKey), -1)));
  const goNextWeek = () => setWeekStartKey(toDateKey(addWeeks(new Date(weekStartKeyRef.current || weekStartKey), 1)));
  const handleSwipeStart = (e) => { swipeStartX.current = e.touches?.[0]?.clientX ?? e.clientX; };
  const handleSwipeEnd = (e) => {
    if (swipeStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? e.clientX;
    const delta = endX - swipeStartX.current;
    const key = weekStartKeyRef.current || weekStartKey;
    if (delta > 50) setWeekStartKey(toDateKey(addWeeks(new Date(key), -1)));
    else if (delta < -50) setWeekStartKey(toDateKey(addWeeks(new Date(key), 1)));
    swipeStartX.current = null;
  };
  const handleMouseDown = (e) => { if (e.button === 0) swipeStartX.current = e.clientX; };
  const handleMouseUp = (e) => {
    if (e.button !== 0 || swipeStartX.current == null) return;
    const delta = e.clientX - swipeStartX.current;
    const key = weekStartKeyRef.current || weekStartKey;
    if (delta > 50) setWeekStartKey(toDateKey(addWeeks(new Date(key), -1)));
    else if (delta < -50) setWeekStartKey(toDateKey(addWeeks(new Date(key), 1)));
    swipeStartX.current = null;
  };

  const currentWeekStart = new Date(weekStartKey);
  const weekDates = getWeekDates(currentWeekStart);
  const monthLabel = `${MONTH_NAMES[currentWeekStart.getMonth()]} ${currentWeekStart.getFullYear()}`;

  const tasksByDate = {};
  let unscheduledCount = 0;
  tasks.forEach(task => {
    if (!task.due_date) {
      unscheduledCount++;
      if (!tasksByDate['unscheduled']) tasksByDate['unscheduled'] = [];
      tasksByDate['unscheduled'].push(task);
      return;
    }
    const key = getDateKey(task.due_date);
    if (!tasksByDate[key]) tasksByDate[key] = [];
    tasksByDate[key].push(task);
  });

  const now = new Date();
  const monthOptions = [];
  for (let i = 0; i <= 6; i++) monthOptions.push(new Date(now.getFullYear(), now.getMonth() + i, 1));
  for (let i = 1; i <= 2; i++) monthOptions.push(new Date(now.getFullYear(), now.getMonth() - i, 1));

  return (
    <div className="tasks-page gnom-style">
      <header className="gnom-header">
        <div className="gnom-header-left">
          <button type="button" className="gnom-week-nav-btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); goPrevWeek(); }} onPointerDown={(e) => e.stopPropagation()} title="Предыдущая неделя" aria-label="Предыдущая неделя">‹</button>
          <div className="gnom-month-dropdown-wrap" ref={monthDropdownRef}>
            <button type="button" className="gnom-month-selector" onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}>
              <span>{monthLabel}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {monthDropdownOpen && (
              <div className="gnom-month-dropdown">
                {monthOptions.map((d) => {
                  const firstOfMonth = new Date(d.getFullYear(), d.getMonth(), 1);
                  const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
                  return (
                    <button key={label} type="button" onClick={() => { setWeekStartKey(toDateKey(firstOfMonth)); setMonthDropdownOpen(false); }}>{label}</button>
                  );
                })}
              </div>
            )}
          </div>
          <button type="button" className="gnom-week-nav-btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); goNextWeek(); }} onPointerDown={(e) => e.stopPropagation()} title="Следующая неделя" aria-label="Следующая неделя">›</button>
        </div>
      </header>
      <div className="gnom-week-grid-wrap" onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onMouseLeave={() => { swipeStartX.current = null; }}>
        <div className="gnom-week-grid">
          {weekDates.map((date) => {
            const key = getDateKey(date);
            const dayTasks = tasksByDate[key] || [];
            return (
              <div key={key} className="gnom-day-cell">
                <div className="gnom-day-header">{formatDayHeader(date)}</div>
                <div className="gnom-day-tasks">
                  {dayTasks.map((task) => (
                    <div key={task.id} className={taskBlockClass(task)} style={taskBlockStyle(task)} onClick={() => onTaskClick(task)} title={task.title}>{taskBlockLine(task)}</div>
                  ))}
                </div>
                <button type="button" className="gnom-add-btn" onClick={() => onAddTask(date)} title="Добавить задачу">+</button>
              </div>
            );
          })}
          <div className="gnom-day-cell unscheduled">
            <div className="gnom-day-header">Задачи {unscheduledCount}</div>
            <div className="gnom-day-tasks">
              {(tasksByDate['unscheduled'] || []).map((task) => (
                <div key={task.id} className={taskBlockClass(task)} style={taskBlockStyle(task)} onClick={() => onTaskClick(task)} title={task.title}>{taskBlockLine(task)}</div>
              ))}
            </div>
            <button type="button" className="gnom-add-btn" onClick={() => onAddTask(null)} title="Добавить задачу без даты">+</button>
          </div>
        </div>
      </div>
    </div>
  );
}
